/* eslint-disable react-hooks/exhaustive-deps */
import React, { useRef, useEffect, useCallback } from "react";

import {
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from "@mui/material";
import { useReactToPrint } from 'react-to-print';
import msgNotificacion from '../../scripts/notification';
import LogoFS from '../../assets/images/logoFS.png';
import Formatter from "../../scripts/formatters";
import '../../styles/solicitudes.css';
import '../../styles/horasExtras.css';

const cellBorderSx = { border: '1px solid #ddd' };

const headerCellSx = {
  ...cellBorderSx,
  fontWeight: 'bold',
  backgroundColor: '#f5f5f5',
  fontSize: '0.72rem',
  lineHeight: 1.15,
  px: 0.75,
  py: 0.75,
};

const headerCellCompactSx = {
  ...headerCellSx,
  whiteSpace: 'normal',
  wordBreak: 'normal',
};

const timeCellSx = {
  ...cellBorderSx,
  whiteSpace: 'nowrap',
  fontSize: '0.82rem',
  px: 0.75,
  py: 0.75,
};

const FormHorasExtrasConsolidadoPdf = ({ data, print, setPrint, filtros }) => {
  const componentRef = useRef(null);

  const formatTime = (timeValue) => {
    if (!timeValue) return '';
    
    try {
      if (typeof timeValue === 'string' && timeValue.includes(':') && !timeValue.includes('T')) {
        const [hours, minutes] = timeValue.split(':');
        const hour = parseInt(hours, 10);
        const min = parseInt(minutes, 10);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour % 12 || 12;
        return `${displayHour}:${min.toString().padStart(2, '0')} ${ampm}`;
      }
      
      if (typeof timeValue === 'string' && timeValue.includes('T')) {
        const timePart = timeValue.split('T')[1];
        if (timePart) {
          const timeWithoutMs = timePart.split('.')[0];
          const [hours, minutes] = timeWithoutMs.split(':');
          const hour = parseInt(hours, 10);
          const min = parseInt(minutes, 10);
          const ampm = hour >= 12 ? 'PM' : 'AM';
          const displayHour = hour % 12 || 12;
          return `${displayHour}:${min.toString().padStart(2, '0')} ${ampm}`;
        }
      }
      
      const date = new Date(timeValue);
      if (!isNaN(date.getTime())) {
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const displayHour = hours % 12 || 12;
        return `${displayHour}:${minutes.toString().padStart(2, '0')} ${ampm}`;
      }
      
      return timeValue;
    } catch (error) {
      return timeValue;
    }
  };

  const handleAfterPrint = useCallback(() => {
    msgNotificacion('Impresión exitosa', 'success', 500);
    setPrint(false);
  }, [setPrint]);

  const handleBeforeContent = useCallback(() => {
    msgNotificacion('Imprimiendo...', 'info', 1000);
  }, []);

  const pageStyle = `
    @page {
      size: letter portrait;
      margin: 10mm 10mm 10mm 10mm;
    }
  `;

  const handlePrint = useReactToPrint({
    documentTitle: 'REPORTE CONSOLIDADO DE HORAS EXTRAS',
    content: () => componentRef.current,
    onAfterPrint: handleAfterPrint,
    onBeforeGetContent: handleBeforeContent,
    pageStyle,
  });

  const triggerPrint = useCallback(() => {
    if (componentRef.current) {
      setTimeout(() => {
        handlePrint()
      }, 500)
    }
  }, [handlePrint]);

  useEffect(() => {
    if (print) {
      triggerPrint()
    }
  }, [print, triggerPrint]);

  const tituloPeriodoPantalla =
    filtros?.fechaIni && filtros?.fechaFin
      ? `${Formatter.dateFormatter(filtros.fechaIni)} — ${Formatter.dateFormatter(filtros.fechaFin)}`
      : filtros?.fechaReferenciaIni && filtros?.fechaReferenciaFin
      ? `${Formatter.dateFormatter(filtros.fechaReferenciaIni)} — ${Formatter.dateFormatter(filtros.fechaReferenciaFin)}`
      : filtros?.fechaIni || filtros?.fechaFin
      ? Formatter.dateFormatter(filtros.fechaIni || filtros.fechaFin)
      : filtros?.fechaReferenciaIni || filtros?.fechaReferenciaFin
      ? Formatter.dateFormatter(filtros.fechaReferenciaIni || filtros.fechaReferenciaFin)
      : data && data.length > 0
      ? (() => {
          const fechas = [...data]
            .map((r) => r.FechaTrabajo)
            .filter(Boolean)
            .sort((a, b) => new Date(a) - new Date(b))
          if (!fechas.length) return null
          return `${Formatter.dateFormatter(fechas[0])} — ${Formatter.dateFormatter(fechas[fechas.length - 1])}`
        })()
      : null;

  return (    
    <div style={{ padding: 1, margin: 1 }} className='no-print'>
      <div ref={componentRef} className='print-container horas-extras-print'>
        <div className='horas-extras-print__body'>
          <div className='horas-extras-print__header'>
            <img src={LogoFS} alt='Logo contrata' height={88} width={190} className='me-3' />

            <h4 className='text-center fw-bolder'>
              <span>
                {filtros?.modo === 'liquidacion'
                  ? 'REPORTE HORAS EXTRAS — LIQUIDACIÓN'
                  : 'REPORTE CONSOLIDADO DE HORAS EXTRAS'}
              </span>
              {filtros?.modo === 'nomina' && (
                <p className='no-solicitud'>
                  <strong>Período:</strong>{' '}
                  {tituloPeriodoPantalla || 'Sin fechas en los registros'}
                </p>
              )}
              {filtros?.modo === 'liquidacion' && (
                <p className='no-solicitud'>
                  <strong>Período:</strong>{' '}
                  {filtros?.fechaIni && filtros?.fechaFin
                    ? `${Formatter.dateFormatter(filtros.fechaIni)} — ${Formatter.dateFormatter(filtros.fechaFin)}`
                    : tituloPeriodoPantalla || '(Indique fechas en la pantalla)'}
                </p>
              )}
              {filtros?.modo === 'liquidacion' && filtros?.idLiquidacion && (
                <p className='no-solicitud'>
                  <strong>Liquidación No.</strong> {filtros.idLiquidacion}
                </p>
              )}
              {filtros?.modo === 'liquidacion' && filtros?.empleadoCodigo && (
                <p className='no-solicitud'>
                  <strong>Empleado:</strong>{' '}
                  {String(filtros.empleadoCodigo).trim()}
                  {filtros?.empleadoNombre
                    ? ` - ${String(filtros.empleadoNombre).trim()}`
                    : ''}
                </p>
              )}
              {filtros?.modo === 'nomina' && filtros?.nomina != null && filtros?.nomina !== '' && (
                <p className='no-solicitud'>
                  <strong>Nómina:</strong> {String(filtros.nomina)} — <strong>No.</strong>{' '}
                  {filtros?.numeroNomina}
                </p>
              )}
              {filtros?.modo === 'nomina' && filtros?.empleadoCodigo && (
                <p className='no-solicitud'>
                  <strong>Empleado:</strong>{' '}
                  {String(filtros.empleadoCodigo).trim()}
                  {filtros?.empleadoNombre
                    ? ` - ${String(filtros.empleadoNombre).trim()}`
                    : ''}
                </p>
              )}
              {filtros?.modo !== 'nomina' && filtros?.modo !== 'liquidacion' && data && data.length > 0 && (
                <p className='no-solicitud'>
                  <strong>Empleado:</strong> {data[0].NombreEmpleado?.toUpperCase()}
                </p>
              )}
              {filtros?.modo !== 'nomina' && filtros?.modo !== 'liquidacion' && (
                <p className='no-solicitud'>
                  <strong>Período:</strong>{' '}
                  {filtros?.fechaIni && filtros?.fechaFin
                    ? `${Formatter.dateFormatter(filtros.fechaIni)} - ${Formatter.dateFormatter(filtros.fechaFin)}`
                    : data && data.length > 0
                    ? `${Formatter.dateFormatter(data[0].FechaTrabajo)} - ${Formatter.dateFormatter(data[data.length - 1].FechaTrabajo)}`
                    : 'Sin datos'}
                </p>
              )}
            </h4>
          </div>

          <TableContainer
            component={Paper}
            className='horas-extras-print__table-wrap mt-2'
            sx={{ boxShadow: 'none', width: '100%', overflow: 'visible', p: 0 }}
          >
            <Table
              size="small"
              aria-label="reporte consolidado"
              className="horas-extras-consolidado-table"
              sx={{ tableLayout: 'fixed', width: '100%' }}
            >
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell align="center" className="col-fecha" sx={headerCellSx}>
                    FECHA
                  </TableCell>
                  <TableCell align="center" className="col-entrada" sx={headerCellSx}>
                    ENTRADA
                  </TableCell>
                  <TableCell align="center" className="col-salida" sx={headerCellSx}>
                    SALIDA
                  </TableCell>
                  <TableCell align="center" className="col-total" sx={headerCellCompactSx}>
                    HORAS
                    <br />
                    EXTRAS
                  </TableCell>
                  <TableCell align="center" className="col-estado" sx={headerCellSx}>
                    ESTADO
                  </TableCell>
                  <TableCell align="center" className="col-motivo" sx={headerCellSx}>
                    MOTIVO
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.map((row, index) => (
                  <TableRow key={`${row.CodEmpleado}_${row.FechaTrabajo}_${index}`}>
                    <TableCell align="center" className="col-fecha" sx={{ ...cellBorderSx, whiteSpace: 'nowrap' }}>
                      {Formatter.dateFormatter(row.FechaTrabajo)}
                    </TableCell>
                    <TableCell align="center" className="col-entrada" sx={timeCellSx}>
                      {row.entrada ? (
                        <div>
                          <div style={{ fontSize: '0.82rem', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                            {formatTime(row.entrada.horaReal)}
                          </div>
                          {row.entrada.horasExtras > 0 && (
                            <div style={{ fontSize: '0.75rem', color: 'green' }}>
                              <strong>+{parseFloat(row.entrada.horasExtras).toFixed(2)}h</strong>
                            </div>
                          )}
                        </div>
                      ) : (
                        <span style={{ color: '#999', whiteSpace: 'nowrap' }}>Sin hora extra</span>
                      )}
                    </TableCell>
                    <TableCell align="center" className="col-salida" sx={timeCellSx}>
                      {row.salida ? (
                        <div>
                          <div style={{ fontSize: '0.82rem', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                            {formatTime(row.salida.horaReal)}
                          </div>
                          {row.salida.horasExtras > 0 && (
                            <div style={{ fontSize: '0.75rem', color: 'green' }}>
                              <strong>+{parseFloat(row.salida.horasExtras).toFixed(2)}h</strong>
                            </div>
                          )}
                        </div>
                      ) : (
                        <span style={{ color: '#999', whiteSpace: 'nowrap' }}>Sin hora extra</span>
                      )}
                    </TableCell>
                    <TableCell align="center" className="col-total" sx={{ ...cellBorderSx, fontWeight: 'bold', color: 'green', whiteSpace: 'nowrap' }}>
                      {row.totalHorasExtras > 0 ? `${parseFloat(row.totalHorasExtras).toFixed(2)}h` : '0.00h'}
                    </TableCell>
                    <TableCell align="center" className="col-estado" sx={{ ...cellBorderSx, whiteSpace: 'nowrap' }}>
                      {row.estado}
                    </TableCell>
                    <TableCell align="left" className="col-motivo" sx={{ ...cellBorderSx }}>
                      {row.motivo || 'Sin motivo especificado'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <div className='horas-extras-print__totales'>
            <div className='d-flex justify-content-center'>
              <div style={{ display: 'flex', gap: '40px', alignItems: 'center', fontSize: '0.88rem' }}>
                <Typography>
                  <strong>Total de días reportados:</strong> {data?.length || 0}
                </Typography>
                <Typography>
                  <strong>Total de horas extras:</strong> {data?.reduce((total, row) => total + (parseFloat(row.totalHorasExtras) || 0), 0).toFixed(2)}h
                </Typography>
              </div>
            </div>
          </div>

          <Grid container spacing={2} className='horas-extras-print__firmas px-4 py-3 mt-2 border-top justify-content-between'>
            <Grid item xs={3} md={3} textAlign='center'>
              <Typography className='text-decoration-underline' fontSize={10}>
                {filtros?.modo === 'nomina' && filtros?.empleadoNombre
                  ? filtros.empleadoNombre
                  : data && data.length > 0
                  ? data[0].NombreEmpleado
                  : ''}
              </Typography>
              <Typography fontWeight='bolder'>Solicita</Typography>
            </Grid>
            <Grid item xs={3} md={3} textAlign='center'>
              <Typography className='text-decoration-underline' fontSize={10}>
                {data && data.length > 0 ? (data[0].JefeInmediato || '_________________') : '_________________'}
              </Typography>
              <Typography fontWeight='bolder'>Autoriza</Typography>
            </Grid>
            <Grid item xs={3} md={3} textAlign='center'>
              <Typography className='text-decoration-underline' fontSize={10}>
                {data && data.length > 0 ? (data[0].JefeRRHH || '_________________') : '_________________'}
              </Typography>
              <Typography fontWeight='bolder'>Recibe RRHH</Typography>
            </Grid>
          </Grid>
        </div>
      </div>
    </div>
  );
};

export default FormHorasExtrasConsolidadoPdf;
